import { useCallback, useEffect, useState } from "react";

// Lists that can grow: the shape every paged endpoint answers with, the search params a list keeps in the URL, and the
// two hooks that make previous/next over opaque cursors behave like pages.

/** One page from the API: rows, the cursor for the next page (null at the end), the total when the screen shows one. */
export type Page<T> = { items: T[]; next: string | null; total?: number | null };

/**
 * The search params every paged list keeps in the URL: the search text, the page cursor, the row that is open, the
 * row the person came back from, and the column the list is sorted by (`sortBy` in @ghezaa/ui). Spread
 * `listSearch(search)` into a route's `validateSearch`.
 */
export type ListSearch = { q?: string; cursor?: string; open?: string; last?: string; sort?: string };

export function listSearch(search: Record<string, unknown>): ListSearch {
  const out: ListSearch = {};
  for (const key of ["q", "cursor", "open", "last", "sort"] as const) {
    const value = search[key];
    if (typeof value === "string" && value !== "") out[key] = value;
  }
  return out;
}

/**
 * A list page remembers its search, filters and page under `key` so that a record's page can link back to the same
 * place, not to the list's first page. Browser back does this by itself; the explicit back link needs help.
 */
export function rememberListSearch(key: string, search: Record<string, unknown>) {
  try {
    const { last: _last, open: _open, ...rest } = search;
    sessionStorage.setItem(`list-search:${key}`, JSON.stringify(rest));
  } catch {
    // Then the back link lands on the list's first page.
  }
}

export function recallListSearch(key: string): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(`list-search:${key}`);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** The value as it was `ms` ago: what a search box sends to the server while someone is still typing. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(handle);
  }, [value, ms]);
  return debounced;
}

export type CursorPages = {
  /** 0 for the first page. */
  index: number;
  /** The ordinal of the first row on this page, for "51–100 of 1,234". */
  from: number;
  hasPrevious: boolean;
  next: (cursor: string) => void;
  previous: () => void;
  /** Back to the first page; call when the search or a filter changes. */
  reset: () => void;
};

/**
 * Previous and next over opaque cursors. The URL holds only the current page's cursor; the cursors of the pages behind
 * it are kept in sessionStorage under `key`, so a refresh or a back lands on the same page with Previous still working.
 */
export function useCursorPages({ key, cursor, take, onCursor }: { key: string; cursor: string | undefined; take: number; onCursor: (cursor: string | undefined) => void }): CursorPages {
  const storageKey = `list:${key}`;
  const read = useCallback((): string[] => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === "string") : [];
    } catch {
      return [];
    }
  }, [storageKey]);
  const write = useCallback(
    (stack: string[]) => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(stack));
      } catch {
        // Nothing to do: Previous just goes to the first page.
      }
    },
    [storageKey],
  );

  const stack = read();
  // A cursor that is not in the stack (a link opened elsewhere) counts as the second page: Previous goes to the start.
  const index = cursor ? (stack.includes(cursor) ? stack.indexOf(cursor) + 1 : 1) : 0;

  return {
    index,
    from: index * take + 1,
    hasPrevious: index > 0,
    next: (nextCursor) => {
      write([...(cursor && !stack.includes(cursor) ? [cursor] : stack.slice(0, index)), nextCursor]);
      onCursor(nextCursor);
    },
    previous: () => onCursor(index <= 1 ? undefined : stack[index - 2]),
    reset: () => {
      write([]);
      onCursor(undefined);
    },
  };
}
